/**
 * localAuthStore.ts — Local JWT-based authentication store for MiniFlow.
 * 
 * This store handles:
 * - Login/logout with the .NET backend
 * - JWT token storage in localStorage
 * - Automatic token refresh
 * - Token validation
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * Get the API base URL for the local MiniFlow server.
 * Uses relative URL to work with the .NET backend.
 */
function getApiBaseUrl(): string {
  // Use relative URL to hit the same server serving the frontend
  return window.location.origin
}

export interface UserInfo {
  email: string
  username?: string
  extraClaims?: Record<string, object>
}

export interface TokenResult {
  success: boolean
  access_token?: string
  refresh_token?: string
  expires_at?: string
  user?: UserInfo
  error?: string
}

const ACCESS_TOKEN_KEY = 'miniflow_access_token'
const REFRESH_TOKEN_KEY = 'miniflow_refresh_token'
const TOKEN_EXPIRY_KEY = 'miniflow_token_expiry'
const USER_KEY = 'miniflow_user'

export const useLocalAuthStore = defineStore('localAuth', () => {
  // State
  const accessToken = ref<string | null>(localStorage.getItem(ACCESS_TOKEN_KEY))
  const refreshToken = ref<string | null>(localStorage.getItem(REFRESH_TOKEN_KEY))
  const tokenExpiry = ref<Date | null>(restoreExpiry())
  const currentUser = ref<UserInfo | null>(restoreUser())
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  function restoreExpiry(): Date | null {
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY)
    if (expiryStr) {
      const date = new Date(expiryStr)
      if (!isNaN(date.getTime())) return date
    }
    return null
  }

  function restoreUser(): UserInfo | null {
    try {
      const raw = localStorage.getItem(USER_KEY)
      return raw ? (JSON.parse(raw) as UserInfo) : null
    } catch {
      return null
    }
  }

  function persistUser(user: UserInfo | null | undefined) {
    if (user) {
      currentUser.value = user
      localStorage.setItem(USER_KEY, JSON.stringify(user))
    }
  }

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value)
  const isTokenExpired = computed(() => {
    if (!tokenExpiry.value) return true
    const now = new Date()
    return now >= tokenExpiry.value
  })
  const hasValidToken = computed(() => 
    isAuthenticated.value && !isTokenExpired.value
  )

  /**
   * Initialize the auth store on app load.
   * Validates existing token and refreshes if needed.
   */
  async function initialize() {
    error.value = null
    
    // Restore tokens from localStorage
    accessToken.value = localStorage.getItem(ACCESS_TOKEN_KEY)
    refreshToken.value = localStorage.getItem(REFRESH_TOKEN_KEY)
    
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY)
    if (expiryStr) {
      tokenExpiry.value = new Date(expiryStr)
    }

    // If token exists and expired, try refresh
    if (accessToken.value && isTokenExpired.value && refreshToken.value) {
      await refreshTokens()
    }

    // Schedule auto-refresh if authenticated
    if (isAuthenticated.value && !isTokenExpired.value) {
      scheduleAutoRefresh()
      void syncSchedulerCredential(refreshToken.value, accessToken.value)
      // Restore user profile if we have a token but no cached user (page reload).
      if (!currentUser.value) {
        void getCurrentUser()
      }
    }
  }

  /**
   * Keeps the background scheduler's credential in sync with the live session token,
   * so scheduled runs always have a valid refresh token (no manual "Connect" needed).
   */
  async function syncSchedulerCredential(
    refresh: string | null,
    access: string | null
  ): Promise<void> {
    if (!refresh || !access) return
    try {
      await fetch(`${getApiBaseUrl()}/api/me/scheduler-credential`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${access}`
        },
        body: JSON.stringify({ refresh_token: refresh })
      })
    } catch {
      /* best-effort; scheduler auth will simply retry on the next token refresh */
    }
  }

  /**
   * Login with email and password.
   */
  async function login(email: string, password: string): Promise<TokenResult> {
    isLoading.value = true
    error.value = null

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      })

      if (!response.ok) {
        let errMsg = 'Login failed'
        try {
          const errData = await response.json()
          errMsg = errData.error || errData.message || errMsg
        } catch { /* ignore parse error */ }
        error.value = errMsg
        return { success: false, error: errMsg }
      }

      const data = await response.json()

      // Backend returns camelCase: accessToken, refreshToken, expires_at, user
      const token = data.accessToken
      const refresh = data.refreshToken
      const expiresAt = data.expires_at
      const user = data.user

      if (!token) {
        error.value = 'No access token received'
        return { success: false, error: error.value }
      }

      // Store tokens
      accessToken.value = token
      refreshToken.value = refresh
      persistUser(user)

      // Store expiry
      if (expiresAt) {
        tokenExpiry.value = new Date(expiresAt)
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt)
      }

      // Persist to localStorage
      localStorage.setItem(ACCESS_TOKEN_KEY, token)
      if (refresh) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
      }

      void syncSchedulerCredential(refresh, token)

      // Schedule auto-refresh
      scheduleAutoRefresh()

      return {
        success: true,
        access_token: token,
        refresh_token: refresh,
        expires_at: expiresAt,
        user: user
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Refresh expired tokens using the refresh token.
   */
  async function refreshTokens(): Promise<boolean> {
    if (!refreshToken.value) {
      logout()
      return false
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken.value })
      })

      if (!response.ok) {
        logout()
        return false
      }

      const data = await response.json()

      // Backend returns camelCase: accessToken, refreshToken, expires_at, user
      const token = data.accessToken
      const refresh = data.refreshToken
      const expiresAt = data.expires_at
      const user = data.user

      if (!token) {
        logout()
        return false
      }

      // Update tokens
      accessToken.value = token
      refreshToken.value = refresh
      persistUser(user)

      if (expiresAt) {
        tokenExpiry.value = new Date(expiresAt)
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt)
      }

      // Persist new tokens
      localStorage.setItem(ACCESS_TOKEN_KEY, token)
      if (refresh) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
      }

      void syncSchedulerCredential(refresh, token)

      // Reschedule auto-refresh
      scheduleAutoRefresh()

      return true
    } catch {
      logout()
      return false
    }
  }

  /**
   * Validate the current access token.
   */
  async function validateToken(token: string): Promise<boolean> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      return response.ok
    } catch {
      return false
    }
  }

  /**
   * Get the current user info.
   */
  async function getCurrentUser(): Promise<UserInfo | null> {
    if (!accessToken.value) return null

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`
        }
      })

      if (!response.ok) {
        return null
      }

      const data = await response.json()
      persistUser(data.user)
      return data.user
    } catch {
      return null
    }
  }

  /**
   * Logout and clear all tokens.
   */
  function logout() {
    accessToken.value = null
    refreshToken.value = null
    currentUser.value = null
    tokenExpiry.value = null
    
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_KEY)
    localStorage.removeItem(USER_KEY)
  }

  /**
   * Get the current access token (refreshing if needed).
   * This should be called before making API requests.
   */
  async function getValidToken(): Promise<string | null> {
    if (!accessToken.value) return null

    if (isTokenExpired.value) {
      const refreshed = await refreshTokens()
      if (!refreshed) return null
    }

    return accessToken.value
  }

  /**
   * Schedule automatic token refresh 5 minutes before expiry.
   */
  function scheduleAutoRefresh() {
    if (!tokenExpiry.value) return

    const now = new Date()
    const expiry = new Date(tokenExpiry.value)
    const timeUntilExpiry = expiry.getTime() - now.getTime()
    
    // Refresh 5 minutes before expiry
    const refreshIn = Math.max(0, timeUntilExpiry - 5 * 60 * 1000)
    
    if (refreshIn > 0) {
      setTimeout(async () => {
        await refreshTokens()
      }, refreshIn)
    }
  }

  /**
   * Check if the user is authenticated and has a valid token.
   */
  async function checkAuth(): Promise<boolean> {
    if (!accessToken.value) {
      return false
    }

    if (isTokenExpired.value) {
      return await refreshTokens()
    }

    return true
  }

  return {
    // State
    accessToken,
    refreshToken,
    currentUser,
    isLoading,
    error,
    tokenExpiry,
    
    // Getters
    isAuthenticated,
    isTokenExpired,
    hasValidToken,
    
    // Actions
    initialize,
    login,
    refreshTokens,
    validateToken,
    getCurrentUser,
    logout,
    getValidToken,
    checkAuth,
    getApiBaseUrl
  }
})
